import React, { useState, useEffect } from 'react';
import { Helmet } from "react-helmet";
import data from '../data/data.json';
import JobBoardComponent from '../components/JobBoardComponent';
import heroImage from "../Assets/heroSection.jpg";

function Careers() {
  const [jobs, setJobs] = useState([]);
  const [filters, setFilters] = useState([]);

  useEffect(() => {
    setJobs(data);
  }, []);

  const filterFunc = ({ role, level, tools, languages }) => {
    if (filters.length === 0) {
      return true;
    }
    const tags = [role, level];
    if (tools) tags.push(...tools);
    if (languages) tags.push(...languages);
    
    return tags.some((tag) => filters.includes(tag));
  };

  const handleTagClick = (tag) => {
    if (filters.includes(tag)) return;
    setFilters([...filters, tag]);
  };

  const handleFilterClick = (passedFilter) => {
    setFilters(filters.filter((f) => f !== passedFilter));
  };

  const clearFilters = () => {
    setFilters([]);
  };

  const filteredJobs = jobs.filter(filterFunc);

  return (
    <div className="w-full bg-[#f8f9fa] text-gray-800 font-sans overflow-x-hidden min-h-screen" data-aos="zoom-in">
      <Helmet>
        <meta charSet="utf-8" />
        <title>Careers | Al Ghaith Properties</title>
        <meta name="description" content="Explore career opportunities at Al Ghaith Properties. Join our team and help shape the future of real estate in Dubai." />
        <link rel="canonical" href="https://www.al-ghaith.ae/#/careers" />
      </Helmet>

      {/* 1. HERO SECTION */}
      <section className="relative w-full h-[70vh] min-h-[500px] flex items-center justify-center border-b border-blue-900/20">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${heroImage})` }}
        />
        <div className="absolute inset-0 bg-blue-900/70 mix-blend-multiply" />
        <div className="absolute inset-0 bg-black/30" />
        
        <div className="relative z-10 text-center px-6 max-w-4xl mx-auto flex flex-col items-center">
          <span className="text-blue-100 tracking-[0.3em] text-sm md:text-base font-semibold mb-6 uppercase mt-16 md:mt-0">
            Careers
          </span>
          <h1 className="text-4xl md:text-6xl lg:text-7xl font-light text-white mb-6 tracking-wider uppercase leading-tight">
            Build Your Future <br className="hidden md:block" /> With Al Ghaith
          </h1>
          <div className="w-24 h-[1px] bg-blue-400 mb-8" />
          <p className="text-lg md:text-xl text-white/90 tracking-widest font-light mb-10 max-w-2xl uppercase">
            Al Ghaith is a place where people can grow.
          </p>
          <button 
            onClick={() => document.getElementById("opportunities-section").scrollIntoView({ behavior: "smooth" })}
            className="bg-white text-blue-900 px-8 py-4 rounded-sm text-sm tracking-widest font-semibold hover:bg-gray-100 transition-colors"
          >
            EXPLORE OPPORTUNITIES
          </button>
        </div>
      </section>

      {/* 2. CAREER INTRODUCTION */}
      <section className="py-24 px-6 bg-white text-center">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-light tracking-[0.2em] mb-8 text-blue-900 uppercase">
            More Than A Job.<br className="hidden md:block"/> A Place To Grow.
          </h2>
          <p className="text-lg md:text-xl text-gray-600 font-light leading-relaxed">
            We believe that our success is built on the talent, dedication, and growth of our people. Join a team committed to excellence and shape the future of real estate with us.
          </p>
        </div>
      </section>

      {/* 3. OPPORTUNITIES SECTION */}
      <section id="opportunities-section" className="py-24 px-6 bg-[#f8f9fa] border-t border-gray-100">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-2xl md:text-3xl font-light tracking-[0.1em] text-blue-900 uppercase mb-4">
              Current Opportunities
            </h2>
            <p className="text-gray-500 font-light text-lg">
              Discover roles where you can make an impact.
            </p>
          </div>

          {/* ACTIVE FILTERS */}
          {filters.length > 0 && (
            <div className="flex flex-wrap items-center justify-center gap-3 mb-12">
              {filters.map((filter, index) => (
                <span
                  key={index}
                  onClick={() => handleFilterClick(filter)}
                  className="group cursor-pointer inline-flex items-center gap-2 bg-blue-900 text-white text-sm tracking-widest uppercase py-2 px-4 rounded-sm transition-colors hover:bg-red-600"
                >
                  {filter}
                  <svg className="w-3 h-3 group-hover:text-white" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd"></path></svg>
                </span>
              ))}
              <button
                onClick={clearFilters}
                className="text-sm tracking-widest text-gray-500 hover:text-blue-900 uppercase font-semibold py-2 px-4 transition-colors underline underline-offset-4"
              >
                Clear All
              </button>
            </div>
          )}

          {/* JOB LISTINGS */}
          <div className="flex flex-col gap-6">
            {jobs.length === 0 ? (
              <div className="text-center py-20 text-gray-500 font-light tracking-widest uppercase">
                Loading opportunities...
              </div>
            ) : filteredJobs.length === 0 ? (
              <div className="text-center py-20 bg-white border border-gray-100 rounded-sm">
                <h3 className="text-xl font-light tracking-widest text-blue-900 mb-4 uppercase">No opportunities found</h3>
                <p className="text-gray-500 font-light mb-8">Try adjusting your filters to see more results.</p>
                <button 
                  onClick={clearFilters}
                  className="border border-blue-900 text-blue-900 px-8 py-3 rounded-sm text-sm tracking-widest font-semibold hover:bg-blue-50 transition-colors"
                >
                  CLEAR FILTERS
                </button>
              </div>
            ) : (
              filteredJobs.map((job) => (
                <JobBoardComponent
                  job={job}
                  key={job.id}
                  handleTagClick={handleTagClick}
                />
              ))
            )}
          </div>
        </div>
      </section>

      {/* 4. FINAL CTA */}
      <section className="py-24 px-6 bg-white border-t border-gray-200 text-center">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-light tracking-[0.1em] text-blue-900 mb-6 uppercase">
            Ready To Take The Next Step?
          </h2>
          <p className="text-gray-600 mb-10 text-lg font-light leading-relaxed">
            If you don't see a role that fits your experience right now, we still want to hear from you. We are always looking for exceptional talent to join our team.
          </p>
          <a 
            href="https://docs.google.com/forms/d/e/1FAIpQLSfXp6T7RRRBNiXfDy926LUaJKa3X-1x61_fxlTG4I8jYxzQmQ/viewform?usp=header"
            target="_blank"
            rel="noreferrer"
            className="inline-block bg-blue-900 text-white px-10 py-4 rounded-sm text-sm tracking-widest font-semibold hover:bg-black transition-colors uppercase shadow-xl"
          >
            Submit General Application
          </a>
        </div>
      </section>

    </div>
  );
}

export default Careers;